import { beatportDeck } from "../decks"

const beatportRedesign = {
  slug: "beatport-redesign",
  title: "Beatport Redesign",
  category: "Academic Project",
  client: "Algonquin College",
  duration: "4 Weeks",
  cover: "/assets/MushaMQRbFC1w6cQEKh1PKvojw.png",
  summary: "A Web Design capstone at Algonquin, rebuilt as a working prototype: real audio, waveforms drawn from the tracks, and eight documented screens for desktop and mobile.",
  gallery: [
    "/assets/MushaMQRbFC1w6cQEKh1PKvojw.png",
    "/assets/pCdrhhgZ8bup07TXcvULkWSOk.png",
    "/assets/f4CrlyLGXLnRrtiNQk7nAwMQTQ.png",
  ],
  blocks: [
    {
      type: "deck",
      id: "beatport-deck",
      title: "The presentation",
      description: "From the audit of Beatport today to the eight new screens, each with numbered decision notes, a dedicated mobile layout and a before-and-after table.",
      deck: beatportDeck,
      note: "An unofficial student concept, not affiliated with or endorsed by Beatport. Outfit stands in for Beatport’s Gravita GEO.",
    },
    {
      type: "video",
      id: "beatport-video",
      title: "The prototype in use",
      description: "89 seconds through the working prototype: browsing, filtering by genre, playing real tracks and building a cart. Sound on.",
      src: "/assets/projects/beatport/prototype-demo.mp4",
      poster: "/assets/projects/beatport/prototype-demo-poster.webp",
    },
    {
      type: "richText",
      html: `<p>A comprehensive UI/UX and brand refresh for Beatport, the world’s leading music platform for DJs and producers. This project was completed as a capstone for Web Design 3, with a focus on creating a seamless, visually compelling, and intuitive digital experience that better serves Beatport’s global community.</p>
<h3><strong>My Approach</strong></h3>
<p>My redesign of Beatport was rooted in a detailed discovery phase, including a thorough audit of the current site, analysis of user needs, and competitive benchmarking within the digital music landscape. I identified key pain points: outdated visual identity, cluttered navigation, poor responsiveness, and a lack of cohesive UI scaling across devices. Leveraging this research, I established a bold new direction anchored in a dark, high-contrast palette and a vibrant neon accent (01FF95) to evoke the energy of club culture.</p>
<p><strong>Key steps included:</strong></p>
<ul>
  <li><p><strong>Content Audit &amp; Structure:</strong> Streamlined the navigation and site architecture for easier access to featured tracks, trending artists, and playlists.</p></li>
</ul>
<ul>
  <li><p><strong>Visual Language:</strong> Developed a unified style guide featuring Gravita GEO as the primary typeface, custom icons, and a modern button system.</p></li>
  <li><p><strong>Moodboard Development:</strong> Gathered visual inspiration from music tech, nightlife, and retro-futurism, setting the tone for immersive discovery.</p></li>
</ul>
<h3><strong>Vision and Innovation</strong></h3>
<p>Beatport’s community is passionate, global, and highly engaged. My vision was to transform the platform into a more intuitive, visually dynamic, and responsive experience that bridges the gap between digital browsing and the excitement of crate-digging in a record shop. I placed strong emphasis on:</p>
<ul>
  <li><p><strong>Immersive Discovery:</strong> High-visibility “Album of the Week,” bold cover art, and integrated streaming/buy options surface new releases while keeping users in flow.</p></li>
</ul>
<ul>
  <li><p><strong>Accessibility &amp; Performance:</strong> Improved hierarchy, larger touch targets, and simplified navigation ensure seamless usability across all devices.</p></li>
  <li><p><strong>Brand Cohesion:</strong> Neon green highlights and a consistent icon system reinforce Beatport’s identity while updating its look for a new generation.</p></li>
</ul>
<h3><strong>Identifying Unique Challenges</strong></h3>
<p>The biggest challenge was balancing Beatport’s extensive catalog and feature set with the need for clarity and speed. The legacy UI was dense and overwhelming, so I prioritized a content structure that delivers core features—music discovery, streaming, downloads—in just a few clicks. The redesign involved stripping away distractions, focusing on user journeys, and future-proofing the design for mobile and desktop use. Achieving a balance between bold, energetic visuals and functional minimalism was central to this process.</p>
<p>This project showcases my end-to-end UX process: from research, strategy, and moodboarding to UI system development and high-fidelity prototyping. The result is a Beatport experience that feels both fresh and familiar—energizing for DJs, accessible to newcomers, and built to scale for the future of digital music.</p>`,
    },
  ],
  relatedSlugs: ["trendless", "animatic-remastered", "seven-due-dates"],
  seo: {
    title: "Beatport Redesign — Matia Dosen",
    description: "UX Designer and Creative",
  },
}

export default beatportRedesign
